import { KonvaGroupNames } from 'components/tool/utils';
import { KONVA_OBS_GROUP } from '../../../../constants';
import { useRefs } from 'contexts/RefContext';
import Konva from 'konva';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Group } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { OBS_EDIT_POINT_ACTION, OBS_SHAPE } from 'store/slices/ToolSlice/types';
import { areCoordinatesEqual, canStartDragFromNode, getAbsolutePoints, getCoordinatesFromShapeWithArea, getHandleHitFunc, 
	getObstructionHandleSize, isPointInPolygon, isPolygonSelfIntersecting, OBSTRUCTION_DEFAULT_FILL, OBSTRUCTION_HANDLE_HIT_STROKE, 
	OBSTRUCTION_INVALID_FILL, willSegmentCreateSelfIntersection } from './utils';
import { removeNewlyCreatedObs, setDrawnObsExists, setInvalidObstructionState, setManualObs, setSelectedObstructionId, setActiveDragFunctionality, setMapOnlyFrozenPoint } from 'store/slices/ToolSlice';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import { pushUndoAction } from 'store/slices/ObstructionUndoSlice';
import { recreateKonvaFromObstruction } from './recreateKonvaFromObstruction';
import { addNewPoint, distanceToEdge } from 'components/tool/Facet/utils';
import { appToast } from 'components/Basic/CustomToast';
import InvalidFacetEditToast from 'components/tool/Group/InvalidModal';

/* -------------------------------------------------------------------------- */
/*                                   Config                                   */
/* -------------------------------------------------------------------------- */

const lineConfig = {
	stroke: 'red',
	strokeWidth: 2,
	dash: [6, 4],
	fill: OBSTRUCTION_DEFAULT_FILL,
	strokeScaleEnabled: false,
};

const HANDLER_SIZE = 7;
const BASE_RADIUS = 4;
const CLICK_THRESHOLD_TO_UNSELECT = 20;
const INACTIVE_HANDLE = {
	fill: '#FFFFFF',
	stroke: 'rgb(255 38 0 / 50%)',
};

const ACTIVE_HANDLE = {
	fill: 'rgb(255 38 0 / 50%)',
	stroke: '#FFFFFF',
};
const ADDED_POINTS_HIGHLIGHT = {
	fill: '#FFC107',
	stroke: '#FFFFFF',
};

const OBSTRUCTION_ADD_POINT_CLICK_THRESHOLD = 10;
const OBSTRUCTION_ADD_POINT_INNER_THRESHOLD = 2;
const OBSTRUCTION_ADD_POINT_HIT_STROKE = 14;
const OBSTRUCTION_DELETE_POINT_HIT_STROKE = 18;
const OBSTRUCTION_DELETE_POINT_CLICK_THRESHOLD = 4;
const INVALID_OBSTRUCTION_INTERSECTION_TOAST_ID = 'obs-invalid-intersection';
const INVALID_OBSTRUCTION_INTERSECTION_TOAST_MESSAGE = 'Please draw a valid polygon.';

// ============ EDITING ZONE THRESHOLD ============
const EDITING_ZONE_THRESHOLD = 20; 

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const distance = (a: number[], b: number[]) => Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2);

const findAncestorMatching = (node: Konva.Node | null, names: string[]): Konva.Node | null => {
	while (node) {
		if (names.includes(node.name())) {
			return node;
		}
		node = node.getParent();
	}
	return null;
};

const toPointKey = (x: number, y: number) => `${x.toFixed(6)},${y.toFixed(6)}`;
const toRoundedPointKey = (x: number, y: number, decimals = 5) => `${x.toFixed(decimals)},${y.toFixed(decimals)}`;
const pointExists = (points: number[], x: number, y: number, decimals = 5) => {
	const targetKey = toRoundedPointKey(x, y, decimals);
	for (let i = 0; i < points.length; i += 2) {
		if (toRoundedPointKey(points[i], points[i + 1], decimals) === targetKey) return true;
	}
	return false;
};

const findClosestVertexIndex = (points: number[], x: number, y: number) => {
	let closestIdx = -1;
	let closestDist = Number.POSITIVE_INFINITY;
	for (let i = 0; i < points.length; i += 2) {
		const dx = points[i] - x;
		const dy = points[i + 1] - y;
		const dist = Math.hypot(dx, dy);
		if (dist < closestDist) {
			closestDist = dist;
			closestIdx = i;
		}
	}
	return { index: closestIdx, distance: closestDist };
};

const findInsertedPointIndex = (
	previous: number[],
	next: number[],
	clickedX: number,
	clickedY: number
) => {
	if (next.length <= previous.length) return -1;
	const tol = 1e-6;
	let i = 0;
	let j = 0;
	while (i < previous.length && j < next.length) {
		const sameX = Math.abs(previous[i] - next[j]) <= tol;
		const sameY = Math.abs(previous[i + 1] - next[j + 1]) <= tol;
		if (sameX && sameY) {
			i += 2;
			j += 2;
			continue;
		}
		return j;
	}
	if (j < next.length) return j;
	// fallback: closest to click if something unexpected happens
	let closestIdx = -1;
	let closestDist = Number.POSITIVE_INFINITY;
	for (let k = 0; k < next.length; k += 2) {
		const dx = next[k] - clickedX;
		const dy = next[k + 1] - clickedY;
		const dist = Math.hypot(dx, dy);
		if (dist < closestDist) {
			closestDist = dist;
			closestIdx = k;
		}
	}
	return closestIdx;
};

const isPointInsidePolygon = (point: { x: number; y: number }, polygon: number[][]) => {
	let inside = false;
	for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
		const xi = polygon[i][0];
		const yi = polygon[i][1];
		const xj = polygon[j][0];
		const yj = polygon[j][1];

		const intersect = ((yi > point.y) !== (yj > point.y)) &&
			(point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);

		if (intersect) inside = !inside;
	}
	return inside;
};

const OBSTRUCTION_BODY_NODE_NAMES = ['obs-rectangle', 'obs-circle', 'obs-line'];
const OBSTRUCTION_DRAG_BLOCKER_NODE_NAMES = [
	'editing-zone',
	'obs-rect-vertex-handle',
	'obs-circle-vertex-handle',
	'obs-polygon-vertex-handle',
];

const createCircleHandle = (cx: number, cy: number, name: string, draggable: boolean, radius: number, strokeWidth: number, visible = true, active = false) => {
	const colors = active ? ACTIVE_HANDLE : INACTIVE_HANDLE;
	return new Konva.Circle({
		x: cx,
		y: cy,
		radius,
		fill: colors.fill,
		stroke: colors.stroke,
		strokeScaleEnabled: false,
		strokeWidth,
		draggable,
		name,
		visible,
	});
};
const createSquareHandle = (cx: number, cy: number, size: number, strokeWidth: number, name: string, draggable: boolean, visible = true, active = false) => {
	const halfSize = size / 2;
	const colors = active ? ACTIVE_HANDLE : INACTIVE_HANDLE;
	return new Konva.Rect({
		x: cx - halfSize,
		y: cy - halfSize,
		width: size,
		height: size,
		fill: colors.fill,
		stroke: colors.stroke,
		strokeWidth,
		strokeScaleEnabled: false,
		draggable,
		name,
		visible,
	});
};

const ObstructionDrawer = () => {
	const { moveStage, rotateStage, addObstructionsMode, obstructionShape, deleteObstructionsMode, manualObs, enableEditObs, selectedObstructionId,
		obstructionEditPointAction, fullScreenEnabled, invalidObstructionIds, hideSatelliteOverlay, mapOnlyFrozenPoint } =
		useSelector((state: RootState) => state.toolNewPostions.data);
	const deletedManuallyAddedObstructionIDs = useSelector((state: RootState) => state.obstructionState.deletedManuallyAddedObstructionIDs);
	const { konvaRef: stageRef } = useRefs();
	const scale = useKonvaImageScale();
	const dispatch = useDispatch<AppDispatch>();
	const manualObsRef = useRef<Konva.Group | null>(null);
	const { isDrawingRef: isDrawing, userDrawnObsRef: userDrawnObs, startPointRef: startPoint } = useRefs();
	const isDragging = useRef<boolean>(false);
	const wasHiddenRef = useRef(hideSatelliteOverlay);

	// When map only turns on mid-drag of any obstruction handle or the whole obstruction
	// group, freeze whatever Konva is currently dragging: snapshot its exact position into
	// the global store and pin it via dragBoundFunc (the drag session is never stopped, so
	// no re-click is needed). On exit, restore the snapshotted value and remove the pin —
	// it sits at its last frozen position until the user moves the mouse, at which point it
	// follows again naturally.
	useEffect(() => {
		const wasHidden = wasHiddenRef.current;
		wasHiddenRef.current = hideSatelliteOverlay;
		if (!isDragging.current) return;
		const node = Konva.DD.node;
		if (!node?.isDragging()) return;

		if (hideSatelliteOverlay && !wasHidden) {
			const frozenPos = node.getAbsolutePosition();
			node.dragBoundFunc(() => frozenPos);
			dispatch(setActiveDragFunctionality('editObstruction'));
			dispatch(setMapOnlyFrozenPoint(frozenPos));
			return;
		}

		if (!hideSatelliteOverlay && wasHidden) {
			if (mapOnlyFrozenPoint) node.setAbsolutePosition(mapOnlyFrozenPoint);
			node.dragBoundFunc(undefined as any);
			dispatch(setActiveDragFunctionality(null));
			dispatch(setMapOnlyFrozenPoint(null));
			node.getLayer()?.batchDraw();
		}
	}, [hideSatelliteOverlay, mapOnlyFrozenPoint, dispatch]);
	const { zoomLevel } = useSelector((state: RootState) => state.toolNewPostions.data);
	const vertexRef = useRef<Record<string, Konva.Shape[]>>({});
	const highlightedManualPointsRef = useRef<Record<string, Set<string>>>({});
	const removedHighlightedManualPointsRef = useRef<Record<string, Set<string>>>({});
	const prevManualPointsRef = useRef<Record<string, number[]>>({});
	const invalidObstructionRef = useRef<Record<string, boolean>>({});
	const shouldListenClickEventsForDrawing = !moveStage && !rotateStage && addObstructionsMode && !hideSatelliteOverlay;
	const shouldListenClickEventsForDeleting = !moveStage && !rotateStage && deleteObstructionsMode && !hideSatelliteOverlay;
	const pendingTouchRef = useRef<{x: number, y: number} | null>(null);
	const zoomJustEndedRef = useRef(false);
	const justCompletedObsRef = useRef(false);
	const suppressNextPolygonClickRef = useRef(false);
	const isEditObsMode = enableEditObs && !deleteObstructionsMode && !hideSatelliteOverlay;
	const isPointEditMode = obstructionEditPointAction === OBS_EDIT_POINT_ACTION.ADD_POINT
		|| obstructionEditPointAction === OBS_EDIT_POINT_ACTION.DELETE_POINT;
	const isTouch = typeof window !== 'undefined' && (
		'ontouchstart' in window || (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0)
	);
	const zoomLevelRef = useRef(zoomLevel);
	const invalidObsCountRef = useRef(invalidObstructionIds.length);
	useEffect(() => {
		zoomLevelRef.current = zoomLevel;
	}, [zoomLevel]);
	useEffect(() => {
		invalidObsCountRef.current = invalidObstructionIds.length;
	}, [invalidObstructionIds.length]);
	const handleZoomMode = fullScreenEnabled ? 'fullScreen' : 'windowed';
	const squareHandleSizeRef = useRef(getObstructionHandleSize(HANDLER_SIZE, zoomLevel, handleZoomMode));
	const circleHandleSizeRef = useRef(getObstructionHandleSize(BASE_RADIUS, zoomLevel, handleZoomMode));
	useEffect(() => {
		squareHandleSizeRef.current = getObstructionHandleSize(HANDLER_SIZE, zoomLevel, handleZoomMode);
	}, [handleZoomMode, zoomLevel]);
	useEffect(() => {
		circleHandleSizeRef.current = getObstructionHandleSize(BASE_RADIUS, zoomLevel, handleZoomMode);
	}, [handleZoomMode, zoomLevel]);
	const selectedObstructionIdRef = useRef<string | null>(selectedObstructionId);
	useEffect(() => {
		selectedObstructionIdRef.current = selectedObstructionId;
	}, [selectedObstructionId]);

	const markJustCompleted = useCallback(() => {
		justCompletedObsRef.current = true;
		setTimeout(() => { justCompletedObsRef.current = false; }, 200);
	}, []);

	const handleFallbackCursor = useMemo(() => {
		if (deleteObstructionsMode || obstructionEditPointAction === OBS_EDIT_POINT_ACTION.DELETE_POINT) {
			return 'pointer';
		}
		if (isEditObsMode && selectedObstructionId) return 'default';
		if (isEditObsMode) return 'crosshair';
		return 'default';
	}, [deleteObstructionsMode, obstructionEditPointAction, isEditObsMode, selectedObstructionId]);

	const setCursor = useCallback((cursor: string) => {
		const container = stageRef?.current?.container();
		if (!container) return;
		if (isDragging.current && cursor !== 'move') return;
		// Guard: never show crosshair while any obs is selected
		if (selectedObstructionIdRef.current && cursor === 'crosshair') {
			container.style.cursor = 'default';
			return;
		}
		// Keep default/crosshair aligned with current edit state.
		if (cursor === 'default' || cursor === 'crosshair') {
			container.style.cursor = handleFallbackCursor;
			return;
		}
		container.style.cursor = cursor;
	}, [handleFallbackCursor, stageRef]);

	const setHandleCursor = useCallback((isOver: boolean) => {
		setCursor(isOver ? 'pointer' : handleFallbackCursor);
	}, [handleFallbackCursor, setCursor]);

	const setFallbackCursor = useCallback(() => {
		setCursor(handleFallbackCursor);
	}, [handleFallbackCursor, setCursor]);

	const applyPolygonValidity = useCallback((
		group: Konva.Group,
		line: Konva.Line,
		points: number[],
		_options: { showToast?: boolean } = {}
	) => {
		const obsId = group.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0];
		const isInvalid = isPolygonSelfIntersecting(points);
		line.fill(isInvalid ? OBSTRUCTION_INVALID_FILL : OBSTRUCTION_DEFAULT_FILL);

		const wasInvalid = invalidObstructionRef.current[obsId] ?? false;
		invalidObstructionRef.current[obsId] = isInvalid;
		if (wasInvalid !== isInvalid) {
			dispatch(setInvalidObstructionState({ id: obsId, invalid: isInvalid }));
		}
		const otherInvalidCount = Math.max(0, invalidObsCountRef.current - ((invalidObstructionRef.current[obsId] ?? false) ? 1 : 0));
		const hasAnyInvalid = isInvalid || otherInvalidCount > 0;
		if (_options.showToast && hasAnyInvalid && isEditObsMode && !deleteObstructionsMode && !appToast.isActive('invalid-obstruction-error')) {
			appToast.sticky(
				<InvalidFacetEditToast shouldDisable={false} />,
				{
					toastId: 'invalid-obstruction-error',
					className: 'invalid-error-toast',
					containerId: 'tool-toast'
				}
			);
		}
		return isInvalid;
	}, [dispatch, isEditObsMode, deleteObstructionsMode]);

	const applyPolygonValidityDuringDrag = useCallback((
		line: Konva.Line,
		points: number[],
		_options: { showToast?: boolean } = {}
	) => {
		const isInvalid = isPolygonSelfIntersecting(points);
		line.fill(isInvalid ? OBSTRUCTION_INVALID_FILL : OBSTRUCTION_DEFAULT_FILL);

		const hasAnyInvalid = isInvalid || invalidObsCountRef.current > 0;
		if (_options.showToast && hasAnyInvalid && isEditObsMode && !deleteObstructionsMode && !appToast.isActive('invalid-obstruction-error')) {
			appToast.sticky(
				<InvalidFacetEditToast shouldDisable={false} />,
				{
					toastId: 'invalid-obstruction-error',
					className: 'invalid-error-toast',
					containerId: 'tool-toast'
				}
			);
		} else if (_options.showToast && !hasAnyInvalid && isEditObsMode && !deleteObstructionsMode && appToast.isActive('invalid-obstruction-error')) {
			appToast.dismiss({
				id: 'invalid-obstruction-error',
				containerId: 'tool-toast'
			});
		}
		return isInvalid;
	}, [isEditObsMode, deleteObstructionsMode]);


	useEffect(() => {
		if (!enableEditObs || deleteObstructionsMode) {
			if (appToast.isActive('invalid-obstruction-error')) {
				appToast.dismiss({
					id: 'invalid-obstruction-error',
					containerId: 'tool-toast'
				});
			}
			return;
		}

		if (invalidObstructionIds.length) {
			if (appToast.isActive('invalid-obstruction-error')) return;
			appToast.sticky(
				<InvalidFacetEditToast
					shouldDisable={!invalidObstructionIds.length}
				/>,
				{
					toastId: 'invalid-obstruction-error',
					className: 'invalid-error-toast',
					containerId: 'tool-toast'
				}
			);
		} else if (appToast.isActive('invalid-obstruction-error')) {
			appToast.dismiss({
				id: 'invalid-obstruction-error',
				containerId: 'tool-toast'
			});
		}
	}, [enableEditObs, deleteObstructionsMode, invalidObstructionIds.length]);

	/* ---------------------------------------------------------------------- */
	/*                          Vertex Management                             */
	/* ---------------------------------------------------------------------- */

	const deleteManualPolygonPoint = useCallback((
		group: Konva.Group,
		line: Konva.Line,
		index: number
	) => {
		if ((line.points().length / 2) <= 3) {
			appToast.warn('Minimum 3 points are required.', {
				autoClose: 2000,
				position: 'bottom-center',
				containerId: 'tool-toast',
				toastId: 'min-obs-points',
			});
			return null;
		}

		const obsId = group.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0];
		const removedKey = toPointKey(line.points()[index], line.points()[index + 1]);
		const currentHighlightSet = highlightedManualPointsRef.current[group.id()];
		if (currentHighlightSet && currentHighlightSet.has(removedKey)) {
			const removedSet = removedHighlightedManualPointsRef.current[group.id()] ?? new Set<string>();
			removedSet.add(removedKey);
			removedHighlightedManualPointsRef.current[group.id()] = removedSet;
		}

		const { coords: beforeCoords, area: beforeArea } = getCoordinatesFromShapeWithArea(line, scale.x, scale.y);
		const beforeState = manualObsStateRef.current[obsId] ?? {
			id: obsId,
			coordinates: beforeCoords,
			userMapped: true,
			area: beforeArea,
			assessment: 'RAW' as const,
			shapeType: 'POLYGON' as const,
		};

		const nextPoints = line.points().filter((_, idx) => idx !== index && idx !== index + 1);
		line.points(nextPoints);
		line.closed(true);

		const { coords, area } = getCoordinatesFromShapeWithArea(line, scale.x, scale.y);
		const afterState = {
			id: obsId,
			coordinates: coords,
			userMapped: true,
			area,
			assessment: 'RAW' as const,
			shapeType: 'POLYGON' as const,
		};
		const isChanged = !areCoordinatesEqual(beforeState.coordinates, afterState.coordinates);
		if (isChanged) {
			dispatch(setManualObs({ newlyAddedObs: afterState }));
			dispatch(pushUndoAction({
				type: 'EDIT',
				obstructionId: obsId,
				before: beforeState,
				after: afterState
			}));
		}
		return nextPoints;
	}, [dispatch, scale.x, scale.y]);

	const updatePolygonVertexHandles = useCallback((group: Konva.Group, points: number[], isDraggable: boolean, visible = true) => {
		if (!group) return;
	
		if(!vertexRef.current[group.id()]) {
			vertexRef.current[group.id()] = [];
		}
		vertexRef.current[group.id()].forEach(c => c.destroy());
		vertexRef.current[group.id()] = [];

		const groupId = group.id();
		const isDeletePointMode = obstructionEditPointAction === OBS_EDIT_POINT_ACTION.DELETE_POINT;
		const highlightSet = highlightedManualPointsRef.current[groupId];
		const prevPoints = prevManualPointsRef.current[groupId];

		const currentKeys = new Set<string>();
		for (let i = 0; i < points.length; i += 2) {
			currentKeys.add(toPointKey(points[i], points[i + 1]));
		}

		const prevKeys = new Set<string>();
		if (prevPoints) {
			for (let i = 0; i < prevPoints.length; i += 2) {
				prevKeys.add(toPointKey(prevPoints[i], prevPoints[i + 1]));
			}
		}

		const nextHighlight = new Set<string>();
		const missingKeys: string[] = [];

		if (highlightSet) {
			highlightSet.forEach((key) => {
				if (currentKeys.has(key)) {
					nextHighlight.add(key);
				} else {
					missingKeys.push(key);
				}
			});
			if (missingKeys.length && prevPoints && prevPoints.length === points.length) {
				const prevKeyToIndex = new Map<string, number>();
				for (let i = 0; i < prevPoints.length; i += 2) {
					prevKeyToIndex.set(toPointKey(prevPoints[i], prevPoints[i + 1]), i);
				}
				const currentIndexToKey = new Map<number, string>();
				for (let i = 0; i < points.length; i += 2) {
					currentIndexToKey.set(i, toPointKey(points[i], points[i + 1]));
				}
				missingKeys.forEach((key) => {
					const idx = prevKeyToIndex.get(key);
					if (idx === undefined) return;
					const newKey = currentIndexToKey.get(idx);
					if (newKey) nextHighlight.add(newKey);
				});
			}
		}

		if (prevPoints && prevPoints.length >= 6 && points.length > prevPoints.length) {
			const removedSet = removedHighlightedManualPointsRef.current[groupId];
			if (removedSet) {
				currentKeys.forEach((key) => {
					if (!prevKeys.has(key) && removedSet.has(key)) {
						nextHighlight.add(key);
						removedSet.delete(key);
					}
				});
			}
		}

		if (highlightSet || nextHighlight.size) {
			highlightedManualPointsRef.current[groupId] = nextHighlight;
		}
		prevManualPointsRef.current[groupId] = points.slice();
	
		for (let i = 0; i < points.length; i += 2) {
			const cx = points[i];
			const cy = points[i + 1];
	
			const size = circleHandleSizeRef.current;
			const vertex = createCircleHandle(
				cx,
				cy,
				'obs-polygon-vertex-handle',
				isDraggable,
				size,
				(1 / zoomLevelRef.current),
				visible,
				isDraggable
			);
			const baseHitStroke = OBSTRUCTION_HANDLE_HIT_STROKE / zoomLevelRef.current;
			const deleteHitStroke = OBSTRUCTION_DELETE_POINT_HIT_STROKE / zoomLevelRef.current;
			const hitStroke = isDeletePointMode ? Math.max(deleteHitStroke, baseHitStroke) : baseHitStroke;
			vertex.hitStrokeWidth(hitStroke);
			vertex.hitFunc(getHandleHitFunc('circle', size, hitStroke));
			if (isDraggable) {
				vertex.on('mouseenter', () => setHandleCursor(true));
				vertex.on('mouseleave', () => setHandleCursor(false));
			}
			const isHighlighted = isDraggable && !!highlightedManualPointsRef.current[groupId]?.has(toPointKey(cx, cy));
			if (isHighlighted) {
				vertex.fill(ADDED_POINTS_HIGHLIGHT.fill);
				vertex.stroke(ADDED_POINTS_HIGHLIGHT.stroke);
			}
	
			if (isDraggable) {
				vertex.on('dragstart', (e) => {
					isDragging.current = true;
					e.cancelBubble = true;
					e.evt?.preventDefault();
				});
				vertex.on('dragmove', (e) => {
					const line = group.findOne('.obs-line') as Konva.Line;
					if (!line) return;

					const newPoints = line.points().slice();
					const prevKey = toPointKey(newPoints[i], newPoints[i + 1]);
					newPoints[i] = vertex.x();
					newPoints[i + 1] = vertex.y();

					line.points(newPoints);
					const highlightSet = highlightedManualPointsRef.current[groupId];
					if (highlightSet && highlightSet.has(prevKey)) {
						const nextKey = toPointKey(vertex.x(), vertex.y());
						highlightSet.delete(prevKey);
						highlightSet.add(nextKey);
						vertex.fill(ADDED_POINTS_HIGHLIGHT.fill);
						vertex.stroke(ADDED_POINTS_HIGHLIGHT.stroke);
					}
					
					// Update editing zone if exists (for selected obs)
					const editingZone = group.findOne('.editing-zone') as Konva.Line;
					if (editingZone) {
						editingZone.points(newPoints);
					}
					applyPolygonValidityDuringDrag(line, newPoints, { showToast: true });
					
					e.cancelBubble = true;
					group.getLayer()?.batchDraw();
				});
	
				vertex.on('dragend', (e) => {
					isDragging.current = false;
					e.cancelBubble = true;
					const line = group.findOne('.obs-line') as Konva.Line | null;
					if (line) {
						applyPolygonValidity(group, line, line.points(), { showToast: true });
					}
					const obsId = group.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0];
					const beforeState = manualObsStateRef.current[obsId];
					const { coords, area } = getCoordinatesFromShapeWithArea(group.findOne('.obs-line')!, scale.x, scale.y);
					const afterState = {
						id: obsId,
						coordinates: coords,
						userMapped: true,
						area,
						assessment: 'RAW' as const,
						shapeType: 'POLYGON' as const,
					};
					// TODO: check if really need to handle this. feedback from the testers.
					// if (area < 2) {
					// 	group.destroy();
					// 	const previewLine = group.findOne('.obs-preview-line') as Konva.Line;
					// 	if (previewLine) previewLine.destroy();
					// 	isDrawing.current = false;
					// 	return;
					// }
					const isChanged = !beforeState || !areCoordinatesEqual(beforeState.coordinates, afterState.coordinates);
					if(isChanged){
						dispatch(setManualObs({ newlyAddedObs: afterState }));
						if (beforeState) {
							dispatch(pushUndoAction({
								type: 'EDIT',
								obstructionId: obsId,
								before: beforeState,
								after: afterState
							}));
						}
					}
				});
			}

			if (isDraggable && isDeletePointMode) {
				vertex.on('click.obsDeletePoint tap.obsDeletePoint', (e) => {
					const line = group.findOne('.obs-line') as Konva.Line | null;
					if (!line) return;
					const nextPoints = deleteManualPolygonPoint(group, line, i);
					if (nextPoints) {
						updatePolygonVertexHandles(group, nextPoints, true, visible);
						group.getLayer()?.batchDraw();
					}
					e.cancelBubble = true;
				});
			}
	
			group.add(vertex);
			vertexRef.current[group.id()] = vertexRef.current[group.id()] || [];
			vertexRef.current[group.id()].push(vertex);
		}
	}, [applyPolygonValidity, applyPolygonValidityDuringDrag, deleteManualPolygonPoint, dispatch, obstructionEditPointAction, scale.x, scale.y]);	

	const updateRectVertexHandles = useCallback(
		(group: Konva.Group | null, rect: Konva.Rect, isDraggable?: boolean, visible = true) => {
			if (!group || !rect) return;

			const groupId = group.id();

			// Destroy existing handles
			if (!vertexRef.current[groupId]) {
				vertexRef.current[groupId] = [];
			}
			vertexRef.current[groupId].forEach(c => c.destroy());
			vertexRef.current[groupId] = [];

			const draggable = !!isDraggable;

			const x = rect.x();
			const y = rect.y();
			const width = rect.width();
			const height = rect.height();
	
			const corners = [
				[x, y],
				[x + width, y],
				[x + width, y + height],
				[x, y + height],
			];

			const size = squareHandleSizeRef.current;
			const halfSize = size / 2;

			corners.forEach(([cx, cy], index) => {
				const handle = createSquareHandle(
					cx, cy, size, (1 / zoomLevelRef.current), 'obs-rect-vertex-handle', draggable, visible, !!isDraggable  
				);
				const hitStroke = OBSTRUCTION_HANDLE_HIT_STROKE / zoomLevelRef.current;
				handle.hitStrokeWidth(hitStroke);
				handle.hitFunc(getHandleHitFunc('square', size, hitStroke));
				if (draggable) {
					handle.on('mouseenter', () => setHandleCursor(true));
					handle.on('mouseleave', () => setHandleCursor(false));
				}
				if (draggable) {
					handle.on('dragstart', (e) => {
						isDragging.current = true;
						e.cancelBubble = true;
						e.evt?.preventDefault();
					});
					handle.on('dragmove', (e) => {
						let newX = rect.x();
						let newY = rect.y();
						let newWidth = rect.width();
						let newHeight = rect.height();
						const vx = handle.x() + halfSize;
						const vy = handle.y() + halfSize;
						switch (index) {
						case 0:
							newWidth = (newX + newWidth) - vx;
							newHeight = (newY + newHeight) - vy;
							newX = vx;
							newY = vy;
							break;
						case 1:
							newWidth = vx - newX;
							newHeight = (newY + newHeight) - vy;
							newY = vy;
							break;
						case 2:
							newWidth = vx - newX;
							newHeight = vy - newY;
							break;
						case 3:
							newWidth = (newX + newWidth) - vx;
							newHeight = vy - newY;
							newX = vx;
							break;
						}

						rect.position({ x: newX, y: newY });
						rect.size({ width: newWidth, height: newHeight });
						
						// Update editing zone if exists
						const editingZone = group.findOne('.editing-zone') as Konva.Rect;
						if (editingZone) {
							const threshold = EDITING_ZONE_THRESHOLD / zoomLevelRef.current;
							editingZone.position({ 
								x: newX - threshold, 
								y: newY - threshold 
							});
							editingZone.size({ 
								width: newWidth + (threshold * 2), 
								height: newHeight + (threshold * 2) 
							});
						}
						
						const updatedCorners = [
							[newX, newY],
							[newX + newWidth, newY],
							[newX + newWidth, newY + newHeight],
							[newX, newY + newHeight],
						];

						vertexRef.current[groupId].forEach((h, idx) => {
							h.position({
								x: updatedCorners[idx][0] - halfSize,
								y: updatedCorners[idx][1] - halfSize,
							});
						});

						e.cancelBubble = true;
						group.getLayer()?.batchDraw();
					});

					handle.on('dragend', (e) => {
						isDragging.current = false;
						e.cancelBubble = true;
						const obsId = groupId.split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0];
						const beforeState = manualObsStateRef.current[obsId];
						const { coords, area } = getCoordinatesFromShapeWithArea(rect, scale.x, scale.y);
						const afterState = {
							id: obsId,
							coordinates: coords,
							userMapped: true,
							area,
							assessment: 'RAW' as const,
							shapeType: 'RECT' as const,
						};
						const isChanged = !beforeState || !areCoordinatesEqual(beforeState.coordinates, afterState.coordinates);
						if(isChanged){
							dispatch(setManualObs({ newlyAddedObs: afterState }));
							if (beforeState) {
								dispatch(pushUndoAction({
									type: 'EDIT',
									obstructionId: obsId,
									before: beforeState,
									after: afterState
								}));
							}
						}
					});
				}

				group.add(handle);
				vertexRef.current[groupId].push(handle);
			});
		},
		[addObstructionsMode, dispatch, scale.x, scale.y]
	);

	const updateCircleVertexHandles = useCallback(
		(group: Konva.Group, circle: Konva.Circle, isDraggable = true, visible = true) => {
			if (!group || !circle) return;

			const id = group.id();

			if (!vertexRef.current[id]) {
				vertexRef.current[id] = [];
			}
			vertexRef.current[id].forEach(c => c.destroy());
			vertexRef.current[id] = [];

			const size = squareHandleSizeRef.current;
			const halfSize = size / 2;

			const createHandle = (lock: 'x' | 'y') => {
				const h = createSquareHandle(
					0, 0, size, (1 / zoomLevelRef.current), 'obs-circle-vertex-handle', isDraggable, visible, isDraggable  
				);
				const hitStroke = OBSTRUCTION_HANDLE_HIT_STROKE / zoomLevelRef.current;
				h.hitStrokeWidth(hitStroke);
				h.hitFunc(getHandleHitFunc('square', size, hitStroke));
				if (isDraggable) {
					h.on('mouseenter', () => setHandleCursor(true));
					h.on('mouseleave', () => setHandleCursor(false));
				}
				h.on('dragstart', (e) => {
					isDragging.current = true;
					e.cancelBubble = true;
					e.evt?.preventDefault();
				});
				h.on('dragmove', (e) => {
					const cx = circle.x();
					const cy = circle.y();

					let hx = h.x() + halfSize;
					let hy = h.y() + halfSize;

					if (lock === 'x') {
						hx = cx;
						h.x(cx - halfSize);
					}
					if (lock === 'y') {
						hy = cy;
						h.y(cy - halfSize);
					}

					const dx = hx - cx;
					const dy = hy - cy;

					const newRadius = Math.max(2, Math.sqrt(dx * dx + dy * dy));
					circle.radius(newRadius);
					// Update editing zone if exists
					const editingZone = group.findOne('.editing-zone') as Konva.Circle;
					if (editingZone) {
						const threshold = EDITING_ZONE_THRESHOLD / zoomLevelRef.current;
						editingZone.radius(newRadius + threshold);
					}

					repositionCircleHandles(group, circle);
					e.cancelBubble = true;
					group.getLayer()?.batchDraw();
				});

				h.on('dragend', (e) => {
					isDragging.current = false;
					e.cancelBubble = true;
					const obsId = group.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0];
					const beforeState = manualObsStateRef.current[obsId];
					const { coords, area } = getCoordinatesFromShapeWithArea(circle, scale.x, scale.y);
					const afterState = {
						id: obsId,
						coordinates: coords,
						userMapped: true,
						area,
						assessment: 'CIRCLE' as const,
						shapeType: 'CIRCLE' as const,
					};
					const isChanged = !beforeState || !areCoordinatesEqual(beforeState.coordinates, afterState.coordinates);
					if(isChanged){
						dispatch(setManualObs({ newlyAddedObs: afterState }));
						if (beforeState) {
							dispatch(pushUndoAction({
								type: 'EDIT',
								obstructionId: obsId,
								before: beforeState,
								after: afterState
							}));
						}
					}
				});

				group.add(h);
				vertexRef.current[id].push(h);
			};

			createHandle('y'); // east
			createHandle('x'); // south
			createHandle('y'); // west
			createHandle('x'); // north

			repositionCircleHandles(group, circle, visible);
		},
		[dispatch, scale.x, scale.y]
	);

	const repositionCircleHandles = (
		group: Konva.Group,
		circle: Konva.Circle,
		visible = true
	) => {
		const handles = vertexRef.current[group.id()];
		if (!handles || handles.length !== 4) return;

		const cx = circle.x();
		const cy = circle.y();
		const r = circle.radius();
		const size = squareHandleSizeRef.current;
		const halfSize = size / 2;

		const positions = [
			{ x: cx + r, y: cy }, // east
			{ x: cx, y: cy + r }, // south
			{ x: cx - r, y: cy }, // west
			{ x: cx, y: cy - r }, // north
		];

		handles.forEach((h, i) => {
			h.position({
				x: positions[i].x - halfSize,
				y: positions[i].y - halfSize,
			});
			h.visible(visible);
		});
	};

	/* ---------------------------------------------------------------------- */
	/*                        Selection Logic                                 */
	/* ---------------------------------------------------------------------- */

	/**
	 * Attaches click/tap selection listeners to a manually drawn obs group.
	 * - Does NOT auto-select on creation — user must explicitly click.
	 */
	const addSelectionHandlers = useCallback((group: Konva.Group, obsId: string) => {
		group.off('click.obsSelection tap.obsSelection');

		group.on('click.obsSelection tap.obsSelection', (e) => {
			if (deleteObstructionsMode) return;
			if (!isEditObsMode) return;
			if (isDrawing.current) return;

			e.cancelBubble = true;

			// Skip the click that fires immediately after draw completion
			if (justCompletedObsRef.current) return;

			if (selectedObstructionId !== obsId) {
				dispatch(setSelectedObstructionId({ id: obsId }));
			}
		});
	}, [deleteObstructionsMode, dispatch, isEditObsMode, selectedObstructionId]);

	/**
	 * Stage-level click handler: deselects current obs when user clicks
	 * outside all obstruction groups (both drawn and saved).
	 */
	useEffect(() => {
		if (!stageRef?.current || !isEditObsMode) return;

		const stage = stageRef.current;
		const idPrefix = (id: string) => `${id}${KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR}`;
		const getSelectedObstructionGroup = (selectedId: string) => {
			const prefix = idPrefix(selectedId);
			return stage.findOne((node: Konva.Node) => {
				if (!(node instanceof Konva.Group)) return false;
				const nodeId = node.id();
				return !!nodeId && (nodeId === selectedId || nodeId.startsWith(prefix));
			}) as Konva.Group | null;
		};

		const isWithinUnselectBuffer = (pointerPos: Konva.Vector2d, selectedId: string) => {
			const threshold = CLICK_THRESHOLD_TO_UNSELECT;
			const group = getSelectedObstructionGroup(selectedId);
			if (!group) return false;

			const withinPolygonBuffer = (polygon: number[]) =>
				isPointInPolygon(pointerPos, polygon) || distanceToEdge(pointerPos, polygon) <= threshold;

			const line = group.findOne('.raw-obs-line, .obs-line') as Konva.Line | null;
			if (line) return withinPolygonBuffer(getAbsolutePoints(line, line.points()));

			const rect = group.findOne('.raw-obs-rect, .obs-rectangle') as Konva.Rect | null;
			if (rect) {
				const rectPoints = getAbsolutePoints(rect, [
					0, 0,
					rect.width(), 0,
					rect.width(), rect.height(),
					0, rect.height(),
				]);
				return withinPolygonBuffer(rectPoints);
			}

			const circle = group.findOne('.raw-obs-circle, .obs-circle') as Konva.Circle | null;
			if (circle) {
				const t = circle.getAbsoluteTransform();
				const c = t.point({ x: 0, y: 0 });
				const px = t.point({ x: circle.radius(), y: 0 });
				const py = t.point({ x: 0, y: circle.radius() });
				const rx = Math.hypot(px.x - c.x, px.y - c.y);
				const ry = Math.hypot(py.x - c.x, py.y - c.y);
				if (rx <= 0 || ry <= 0) return false;

				const dx = pointerPos.x - c.x;
				const dy = pointerPos.y - c.y;
				const d = Math.hypot(dx, dy);
				if (d === 0) return true;

				const ux = dx / d;
				const uy = dy / d;
				const r = 1 / Math.sqrt((ux * ux) / (rx * rx) + (uy * uy) / (ry * ry));
				return d <= r || Math.abs(d - r) <= threshold;
			}

			return false;
		};

		const handleStageClickForDeselect = (e?: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
			if (!selectedObstructionId) return;
			if (deleteObstructionsMode) return;
			if (isDrawing.current) return;
			if (stage.getAttr('isZooming') || zoomJustEndedRef.current) return;
			if (typeof stage.isDragging === 'function' && stage.isDragging()) return;
			if (e?.evt && 'touches' in e.evt && e.evt.touches && e.evt.touches.length > 1) return;

			const pointerPos = stage.getPointerPosition();
			if (!pointerPos) return;

			const intersections = stage.getAllIntersections(pointerPos);

			const clickedInsideAnyObs = intersections.some((node) =>
				node.findAncestor(`.${KonvaGroupNames.obstructionsGroup}`) ||
				node.findAncestor(`.${KonvaGroupNames.obstructionDrawingGroup}`)
			);

			const withinBuffer = !clickedInsideAnyObs
				? isWithinUnselectBuffer(pointerPos, selectedObstructionId)
				: false;

			if (!clickedInsideAnyObs && !withinBuffer) {
				dispatch(setSelectedObstructionId({ id: null }));
				suppressNextPolygonClickRef.current = true;
				requestAnimationFrame(() => {
					suppressNextPolygonClickRef.current = false;
				});
			}
		};

		stage.on('click.obsDeselect tap.obsDeselect', handleStageClickForDeselect);

		return () => {
			stage.off('click.obsDeselect tap.obsDeselect', handleStageClickForDeselect);
		};
	}, [stageRef, isEditObsMode, selectedObstructionId, deleteObstructionsMode, dispatch, shouldListenClickEventsForDrawing, isDrawing]);

	/* ---------------------------------------------------------------------- */
	/*                           Drag Handling                                */
	/* ---------------------------------------------------------------------- */

	const addDragHandlers = useCallback((group: Konva.Group) => {
		group.off('dragstart dragend');
		group.off('mousedown.dragCheck touchstart.dragCheck mouseup.dragCheck touchend.dragCheck');
		group.dragDistance(isTouch && isPointEditMode ? 8 : 0);
		group.draggable(true);
		group.setAttr('dragAllowed', false);

		group.on('mousedown.dragCheck touchstart.dragCheck', (e) => {
			const target = e.target;
			const canDrag = canStartDragFromNode(
				target,
				OBSTRUCTION_BODY_NODE_NAMES,
				OBSTRUCTION_DRAG_BLOCKER_NODE_NAMES
			);
			group.setAttr('dragAllowed', canDrag);
		});

		group.on('mouseup.dragCheck touchend.dragCheck', () => {
			group.setAttr('dragAllowed', false);
		});

		group.on('dragstart', (e) => {
			if (group.getAttr('dragAllowed') !== true) {
				group.stopDrag();
				return;
			}
			if (stageRef?.current?.getAttr('isZooming')) {
				group.stopDrag();
				return;
			}
			if (isDrawing.current) {
				group.stopDrag();
				return;
			}
			if (stageRef?.current) {
				isDragging.current = true;
				setCursor('move');
			}
		});
	
		group.on('dragend', (e) => {
			isDragging.current = false;
			setFallbackCursor();
			group.setAttr('dragAllowed', false);
			if (e.target !== group) return;

			if (!manualObsRef.current) return;

			const obsId = group.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0];
			const beforeState = manualObsStateRef.current[obsId]; // Grab the state BEFORE the dispatch

			const pos = group.position();
			group.position({ x: 0, y: 0 });

			const rect = group.findOne('.obs-rectangle') as Konva.Rect | null;
			const circle = group.findOne('.obs-circle') as Konva.Circle | null;
			const line = group.findOne('.obs-line') as Konva.Line | null;
	
			if (rect) {
				rect.x(rect.x() + pos.x);
				rect.y(rect.y() + pos.y);
	
				const { coords, area } = getCoordinatesFromShapeWithArea(rect, scale.x, scale.y);
				const afterState = {
					id: obsId,
					coordinates: coords,
					userMapped: true,
					area,
					assessment: 'RAW' as const,
					shapeType: 'RECT' as const,
				};
				const isChanged = !beforeState || !areCoordinatesEqual(beforeState.coordinates, afterState.coordinates);
				if(isChanged){
					dispatch(setManualObs({newlyAddedObs: afterState}));
					if (beforeState) {
						dispatch(pushUndoAction({ type: 'EDIT', obstructionId: obsId, before: beforeState, after: afterState }));
					}
					updateRectVertexHandles(group, rect, true);
				}
			} 
			else if (circle) {
				circle.x(circle.x() + pos.x);
				circle.y(circle.y() + pos.y);
	
				const { coords, area } = getCoordinatesFromShapeWithArea(circle, scale.x, scale.y);
				const afterState = {
					id: obsId,
					coordinates: coords,
					userMapped: true,
					area,
					assessment: 'CIRCLE' as const,
					shapeType: 'CIRCLE' as const,
				};
				const isChanged = !beforeState || !areCoordinatesEqual(beforeState.coordinates, afterState.coordinates);
				if(isChanged){
					dispatch(setManualObs({newlyAddedObs: afterState}));
					if (beforeState) {
						dispatch(pushUndoAction({ type: 'EDIT', obstructionId: obsId, before: beforeState, after: afterState }));
					}
					updateCircleVertexHandles(group, circle, true);
				}
			} 
			else if (line) {
				const updatedPoints = line.points().map((p, i) =>
					i % 2 === 0 ? p + pos.x : p + pos.y
				);

				line.points(updatedPoints);

				const { coords, area } = getCoordinatesFromShapeWithArea(line, scale.x, scale.y);
				const afterState = {
					id: obsId,
					coordinates: coords,
					userMapped: true,
					area,
					assessment: 'RAW' as const,
					shapeType: 'POLYGON' as const,
				};
				const isChanged = !beforeState || !areCoordinatesEqual(beforeState.coordinates, afterState.coordinates);
				if(isChanged){
					dispatch(setManualObs({newlyAddedObs: afterState}));
					if (beforeState) {
						dispatch(pushUndoAction({ type: 'EDIT', obstructionId: obsId, before: beforeState, after: afterState }));
					}
					updatePolygonVertexHandles(group, updatedPoints, true);
				}
			}

			// Ensure cursor stays consistent after drag in edit mode
			setFallbackCursor();
		});
	}, [dispatch, handleFallbackCursor, isDrawing, isPointEditMode, isTouch, scale.x, scale.y, setCursor, stageRef, updateCircleVertexHandles, updatePolygonVertexHandles, updateRectVertexHandles]);

	/* ---------------------------------------------------------------------- */
	/*                     Pointer / Touch Logic                              */
	/* ---------------------------------------------------------------------- */

	const handleManualPolygonAddPoint = useCallback((group: Konva.Group, line: Konva.Line, e: Konva.KonvaEventObject<Event>) => {
		if (!isEditObsMode) return;
		if (deleteObstructionsMode) return;
		if (obstructionEditPointAction !== OBS_EDIT_POINT_ACTION.ADD_POINT) return;

		const obsId = group.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0];
		if (selectedObstructionId !== obsId) {
			return;
		}
		if (!line.closed() && !group.getAttr('closed')) {
			return;
		}

		const stage = stageRef?.current ?? line.getStage();
		if (!stage) {
			return;
		}
		const absPointerPos = stage.getRelativePointerPosition();
		if (!absPointerPos) {
			return;
		}

		const linePoints = line.points();
		if (linePoints.length < 6) {
			return;
		}

		const screenPolygon: number[][] = [];
		for (let i = 0; i < linePoints.length; i += 2) {
			screenPolygon.push([linePoints[i], linePoints[i + 1]]);
		}

		const zoom = Math.max(0.05, zoomLevelRef.current || 1);
		const addPointThreshold = OBSTRUCTION_ADD_POINT_CLICK_THRESHOLD * zoom;
		const innerThreshold = OBSTRUCTION_ADD_POINT_INNER_THRESHOLD * zoom;
		const dist = distanceToEdge(absPointerPos, linePoints);
		if (dist > addPointThreshold) {
			return;
		}

		const isInside = isPointInsidePolygon({ x: absPointerPos.x, y: absPointerPos.y }, screenPolygon);
		if (isInside && dist > innerThreshold) {
			return;
		}

		const previousPoints = linePoints.slice();
		const { coords: currentCoords, area: currentArea } = getCoordinatesFromShapeWithArea(line, scale.x, scale.y);
		const beforeState = manualObsStateRef.current[obsId] ?? {
			id: obsId,
			coordinates: currentCoords,
			userMapped: true,
			area: currentArea,
			assessment: 'RAW' as const,
			shapeType: 'POLYGON' as const,
		};
		const newCoords = addNewPoint(absPointerPos, currentCoords, scale);
		const newPoints = newCoords.flatMap(([x, y]) => [x * scale.x, y * scale.y]);

		line.points(newPoints);
		line.closed(true);
		applyPolygonValidity(group, line, newPoints, { showToast: true });
		const insertedIndex = findInsertedPointIndex(previousPoints, newPoints, absPointerPos.x, absPointerPos.y);
		if (insertedIndex >= 0) {
			const key = toPointKey(newPoints[insertedIndex], newPoints[insertedIndex + 1]);
			const groupId = group.id();
			const set = highlightedManualPointsRef.current[groupId] ?? new Set<string>();
			set.add(key);
			highlightedManualPointsRef.current[groupId] = set;
		}
		updatePolygonVertexHandles(group, newPoints, true, !deleteObstructionsMode);
		group.getLayer()?.batchDraw();

		const { area } = getCoordinatesFromShapeWithArea(line, scale.x, scale.y);
		const afterState = {
			id: obsId,
			coordinates: newCoords,
			userMapped: true,
			area,
			assessment: 'RAW' as const,
			shapeType: 'POLYGON' as const,
		};
		const isChanged = !areCoordinatesEqual(beforeState.coordinates, afterState.coordinates);
		if (isChanged) {
			dispatch(setManualObs({ newlyAddedObs: afterState }));
			dispatch(pushUndoAction({
				type: 'EDIT',
				obstructionId: obsId,
				before: beforeState,
				after: afterState
			}));
		}

		e.cancelBubble = true;
	}, [applyPolygonValidity, deleteObstructionsMode, dispatch, obstructionEditPointAction, scale.x, scale.y, selectedObstructionId, stageRef, updatePolygonVertexHandles, isEditObsMode]);

	const handleManualPolygonDeletePoint = useCallback((group: Konva.Group, line: Konva.Line, e: Konva.KonvaEventObject<Event>) => {
		if (!isEditObsMode) return;
		if (deleteObstructionsMode) return;
		if (obstructionEditPointAction !== OBS_EDIT_POINT_ACTION.DELETE_POINT) return;

		const obsId = group.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0];
		if (selectedObstructionId !== obsId) {
			return;
		}

		const stage = stageRef?.current ?? line.getStage();
		if (!stage) {
			return;
		}
		const absPointerPos = stage.getRelativePointerPosition();
		if (!absPointerPos) {
			return;
		}

		const { index, distance } = findClosestVertexIndex(line.points(), absPointerPos.x, absPointerPos.y);
		const zoom = Math.max(0.05, zoomLevelRef.current || 1);
		const deleteThreshold = OBSTRUCTION_DELETE_POINT_CLICK_THRESHOLD * zoom;
		if (index < 0 || distance > deleteThreshold) {
			return;
		}

		const nextPoints = deleteManualPolygonPoint(group, line, index);
		if (nextPoints) {
			applyPolygonValidity(group, line, nextPoints, { showToast: true });
			updatePolygonVertexHandles(group, nextPoints, true, !deleteObstructionsMode);
			group.getLayer()?.batchDraw();
		}
		e.cancelBubble = true;
	}, [applyPolygonValidity, deleteManualPolygonPoint, deleteObstructionsMode, isEditObsMode, obstructionEditPointAction, selectedObstructionId, stageRef, updatePolygonVertexHandles]);

	const drawRectanglePointerDown = useCallback(() => {
		if (isDragging.current) return;
		if (!stageRef?.current) return;
		if (stageRef?.current?.getAttr('isZooming'))	return;

		const pointerPos = stageRef.current.getPointerPosition();
		if (!pointerPos) return;
	
		const shape = stageRef.current.getIntersection(pointerPos);
		if (shape && !isDrawing.current) {
			const parentGroup = findAncestorMatching(shape, [KonvaGroupNames.inProgressUserDefinedObsGroup, KonvaGroupNames.obstructionDrawingGroup]);
			if (parentGroup) {
				return;
			}
			// Prevent drawing when clicking on saved obstructions in edit mode
			if (isEditObsMode) {
				const savedObsGroup = findAncestorMatching(shape, [KonvaGroupNames.obstructionsGroup]);
				if (savedObsGroup) {
					return;
				}
			}
		}

		const { x, y } = stageRef.current.getRelativePointerPosition() as Vector2d;
		if(!startPoint.current){
			startPoint.current = { x, y };
		}
		const children = manualObsRef?.current?.children;
		if (children?.length === 0) {
			isDrawing.current = false;
		} else {
			const lastChild = children?.[children?.length - 1] as Konva.Group;
			const isRectGroup =
				lastChild.name() === KonvaGroupNames.inProgressUserDefinedObsGroup &&
				lastChild.findOne('.obs-rectangle') as Konva.Rect;
			if (isRectGroup && !isRectGroup.getAttr('completed')) {
				if (!userDrawnObs?.current) return;
				const width = isRectGroup.width();
				const height = isRectGroup.height();
			
				// Complete the incomplete rect obs
				if (Math.abs(width) == 0 || Math.abs(height) == 0) {
					// complete the obs from the last pointer position
					const rect = userDrawnObs.current.findOne('.obs-rectangle') as Konva.Rect;
	
					const width = x - startPoint.current.x;
					const height = y - startPoint.current.y;
	
					rect.width(width);
					rect.height(height);
					rect.x(startPoint.current.x);
					rect.y(startPoint.current.y);
					updateRectVertexHandles(userDrawnObs.current, rect, false, true);
				}
				const {coords, area} = getCoordinatesFromShapeWithArea(isRectGroup, scale.x, scale.y);
				dispatch(setManualObs({
					newlyAddedObs: {
						id: userDrawnObs.current.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0],
						coordinates: coords,
						userMapped: true,
						area,
						assessment: 'RAW',
						shapeType: 'RECT',
					}
				}));
				dispatch(pushUndoAction({
					type: 'ADD',
					obstructionId: userDrawnObs.current.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0],
				}));
				userDrawnObs.current.draggable(false);
				isRectGroup.setAttr('completed', true);
				updateRectVertexHandles(userDrawnObs.current, isRectGroup, false, true);
				isDrawing.current = false;
				startPoint.current = null;
				userDrawnObs.current = null;
				markJustCompleted();
				return;
			}
		}

		const uuid = crypto.randomUUID();
		const group = new Konva.Group({
			id: uuid + KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR + KONVA_OBS_GROUP.IN_PROGRESS_GROUP_NAME,
			name: KonvaGroupNames.inProgressUserDefinedObsGroup,
			draggable: false,
		});

		const rect = new Konva.Rect({
			x,
			y,
			width: 0,
			height: 0,
			stroke: 'red',
			strokeWidth: 2,
			dash: [6, 4],
			fill: OBSTRUCTION_DEFAULT_FILL,
			strokeScaleEnabled: false,
			name: 'obs-rectangle',
			id: uuid + KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR + KONVA_OBS_GROUP.RECT_ID_SUFFIX,
		});
		group.add(rect);
		userDrawnObs.current = group;
		manualObsRef.current?.add(group as Konva.Group);
		dispatch(setDrawnObsExists({ exists: true }));
		isDrawing.current = true;
	}, [dispatch, isDrawing, isEditObsMode, scale.x, scale.y, stageRef, startPoint, updateRectVertexHandles, userDrawnObs, markJustCompleted]);

	const drawPolygonPointerDown = useCallback(() => {
		if (suppressNextPolygonClickRef.current) return;
		if (isEditObsMode && !!selectedObstructionId) return;
		if (stageRef?.current?.getAttr('isZooming')) return;
		if (zoomJustEndedRef.current) return;
		if(obstructionShape !== OBS_SHAPE.POLYGON) return;
		if (isDragging.current) return;
		if (!stageRef?.current) return;
		const pointerPos = stageRef.current.getPointerPosition();
		if (!pointerPos) return;
		const shape = stageRef.current.getIntersection(pointerPos);
		if (shape && !isDrawing.current) {
			const parentGroup = findAncestorMatching(shape, [KonvaGroupNames.inProgressUserDefinedObsGroup, KonvaGroupNames.obstructionDrawingGroup]);
			if (parentGroup) {
				return;
			}
			// Prevent drawing when clicking on saved obstructions in edit mode
			if (isEditObsMode) {
				const savedObsGroup = findAncestorMatching(shape, [KonvaGroupNames.obstructionsGroup]);
				if (savedObsGroup) {
					return;
				}
			}
		}

		const { x, y } = stageRef.current.getRelativePointerPosition() as Vector2d;
		const children = manualObsRef?.current?.children;
		if (children?.length === 0) {
			isDrawing.current = false;
		} else {
			const lastChild = children?.[children?.length - 1] as Konva.Group;
			const isPolygonGroup =
				lastChild.name() === KonvaGroupNames.inProgressUserDefinedObsGroup &&
				lastChild.findOne('.obs-preview-line');
	
			if (isPolygonGroup) {
				isDrawing.current = !lastChild?.attrs?.closed;
			} else {
				isDrawing.current = false;
			}
		}
		if (isDrawing.current === false) {
			userDrawnObs.current = null;
			const uuid = crypto.randomUUID();
			userDrawnObs.current = new Konva.Group({
				name:  KonvaGroupNames.inProgressUserDefinedObsGroup,
				id: uuid + KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR + KONVA_OBS_GROUP.IN_PROGRESS_GROUP_NAME,
				draggable: false,
			});
			const previewLine = new Konva.Line({ name: 'obs-preview-line', ...lineConfig, points: [] });
			const facetLine = new Konva.Line(
				{
					...lineConfig,
					points: [],
					id: uuid + KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR + KONVA_OBS_GROUP.LINE_ID_SUFFIX,
					name: 'obs-line',
				});

			userDrawnObs?.current?.add(facetLine);
			userDrawnObs?.current?.add(previewLine);
			manualObsRef.current?.add(userDrawnObs.current as Konva.Group);
			dispatch(setDrawnObsExists({ exists: true }));
		}

		const facetLine = userDrawnObs?.current?.children?.[0] as Konva.Line;
		const previewLine = userDrawnObs?.current?.findOne('.obs-preview-line') as Konva.Line;
		const currentPoints = facetLine?.points() ?? [];
		const isClosingClick = currentPoints.length > 2 && distance([x, y], [currentPoints[0], currentPoints[1]]) < 2;
		const candidatePoint: [number, number] = isClosingClick ? [currentPoints[0], currentPoints[1]] : [x, y];

		// Prevent adding a duplicate vertex 
		if (!isClosingClick && pointExists(currentPoints, candidatePoint[0], candidatePoint[1], 5)) {
			return;
		}

		if (willSegmentCreateSelfIntersection(currentPoints, candidatePoint, isClosingClick)) {
			appToast.warn(INVALID_OBSTRUCTION_INTERSECTION_TOAST_MESSAGE, {
				autoClose: 2000,
				position: 'bottom-center',
				containerId: 'tool-toast',
				toastId: INVALID_OBSTRUCTION_INTERSECTION_TOAST_ID,
			});
			return;
		}

		if (isClosingClick && userDrawnObs.current) {
			facetLine.closed(true);
			userDrawnObs.current?.setAttr('closed', true);
			previewLine?.remove();
			applyPolygonValidity(userDrawnObs.current, facetLine, currentPoints);
			updatePolygonVertexHandles(userDrawnObs.current, currentPoints, false, true);

			const pts = currentPoints.slice();
			const p: number[][] = [];
			pts.forEach((v, i) => {
				const ind = Math.floor(i / 2);
				if (i % 2 === 0) p.push([v]);
				else p[ind].push(v);
			});
			const { coords, area} = getCoordinatesFromShapeWithArea(facetLine, scale.x, scale.y);

			if (area <= 0) {
				userDrawnObs.current.destroy();
				if (previewLine) previewLine.destroy();
				isDrawing.current = false;
				return;
			}
			dispatch(setManualObs({
				newlyAddedObs: {
					id: userDrawnObs.current.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0],
					coordinates: coords,
					userMapped: true,
					area: area,
					assessment: 'RAW',
					shapeType: 'POLYGON',
				}
			}));
			dispatch(pushUndoAction({
				type: 'ADD',
				obstructionId: userDrawnObs.current.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0],
			}));
			userDrawnObs.current.draggable(false);
			isDrawing.current = false;
			markJustCompleted();
			return;
		}

		currentPoints.push(+x, +y);
		isDrawing.current = true;
		facetLine.points(currentPoints);
		if(userDrawnObs.current) {
			updatePolygonVertexHandles(userDrawnObs.current, currentPoints, false, true);
		}
		// facetLine.on('contextmenu', handlePolygonClick);
		// facetLine.on('touchstart', handleTouchStart);
		// facetLine.on('touchend', handleTouchEndOrCancel);
	}, [applyPolygonValidity, dispatch, isDrawing, isEditObsMode, obstructionShape, scale.x, scale.y, stageRef, updatePolygonVertexHandles, userDrawnObs, selectedObstructionId, markJustCompleted]);

	const drawCirclePointerDown = useCallback(() => {
		if (isDragging.current) return;
		if (!stageRef?.current) return;
		if (stageRef?.current?.getAttr('isZooming'))	return;

		const pointerPos = stageRef.current.getPointerPosition();
		if (!pointerPos) return;
	
		const shape = stageRef.current.getIntersection(pointerPos);
		if (shape && !isDrawing.current) {
			const parentGroup = findAncestorMatching(shape, [KonvaGroupNames.inProgressUserDefinedObsGroup, KonvaGroupNames.obstructionDrawingGroup]);
			if (parentGroup) {
				return;
			}
			// Prevent drawing when clicking on saved obstructions in edit mode
			if (isEditObsMode) {
				const savedObsGroup = findAncestorMatching(shape, [KonvaGroupNames.obstructionsGroup]);
				if (savedObsGroup) {
					return;
				}
			}
		}

		const { x, y } = stageRef.current.getRelativePointerPosition() as Vector2d;
		if(!startPoint.current){
			startPoint.current = { x, y };
		}
		const children = manualObsRef?.current?.children;
		if (children?.length === 0) {
			isDrawing.current = false;
		} else {
			const lastChild = children?.[children?.length - 1] as Konva.Group;
			const isCircleGroup =
				lastChild.name() === KonvaGroupNames.inProgressUserDefinedObsGroup &&
				lastChild.findOne('.obs-circle') as Konva.Circle;
			if (isCircleGroup && !isCircleGroup.getAttr('completed')) {
				if (!userDrawnObs?.current) return;
				const radius = isCircleGroup.radius();

				// Complete the incompleted circle obs
				if (radius <= 0) {
					const circle = userDrawnObs.current.findOne('.obs-circle') as Konva.Circle;
				
					const dx = x - startPoint.current.x;
					const dy = y - startPoint.current.y;
					const radius = Math.sqrt(dx * dx + dy * dy);
				
					circle.radius(radius);
				} 
				const { coords, area } = getCoordinatesFromShapeWithArea(isCircleGroup, scale.x, scale.y);
				dispatch(setManualObs({
					newlyAddedObs: {
						id: userDrawnObs.current.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0],
						coordinates: coords,
						userMapped: true,
						area,
						assessment: 'CIRCLE',
						shapeType: 'CIRCLE',
					}
				}));
				dispatch(pushUndoAction({
					type: 'ADD',
					obstructionId: userDrawnObs.current.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0],
				}));
				userDrawnObs.current.draggable(false);
				isCircleGroup.setAttr('completed', true);
	
				isDrawing.current = false;
				startPoint.current = null;
				userDrawnObs.current = null;
				markJustCompleted();
				return;
			}
		}
		
		const uuid = crypto.randomUUID();
		const group = new Konva.Group({
			id: uuid + KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR + KONVA_OBS_GROUP.IN_PROGRESS_GROUP_NAME,
			name: KonvaGroupNames.inProgressUserDefinedObsGroup,
			draggable: false,
		});

		const circle = new Konva.Circle({
			x,
			y,
			radius: 0,
			stroke: 'red',
			strokeWidth: 2,
			dash: [6, 4],
			fill: OBSTRUCTION_DEFAULT_FILL,
			strokeScaleEnabled: false,
			name: 'obs-circle',
			id: uuid + KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR + KONVA_OBS_GROUP.CIRCLE_ID_SUFFIX,
		});
		group.add(circle);
		userDrawnObs.current = group;
		manualObsRef.current?.add(group as Konva.Group);
		dispatch(setDrawnObsExists({ exists: true }));
		isDrawing.current = true;
		if(userDrawnObs.current) {
			updateCircleVertexHandles(userDrawnObs.current, circle, false, true);
		}
	}, [dispatch, isDrawing, isEditObsMode, scale.x, scale.y, stageRef, startPoint, updateCircleVertexHandles, userDrawnObs, markJustCompleted]);	

	const drawPolygonPointerMove = useCallback(() => {
		if (!stageRef?.current || !userDrawnObs?.current) return;
		const { x, y } = stageRef.current.getRelativePointerPosition() as Vector2d;
		const obsLine = userDrawnObs?.current?.children?.[0] as Konva.Line;
		const previewLine = userDrawnObs?.current?.findOne('.obs-preview-line') as Konva.Line;
		if(!obsLine || !previewLine) return;
		const points = obsLine?.points() ?? [];
		if (points.length >= 2) {
			const startX = points[points.length - 2], startY = points[points.length - 1];
			previewLine?.points([startX, startY, x, y]);
			previewLine?.visible(true);
		}	
	}, [stageRef, userDrawnObs]);

	const drawRectanglePointerMove = useCallback(() => {
		if (!stageRef?.current || !isDrawing.current || !startPoint.current || !userDrawnObs?.current 
			|| !userDrawnObs?.current?.children?.length) return;
		const { x, y } = stageRef.current.getRelativePointerPosition() as Vector2d;
		const rect = userDrawnObs.current.findOne('.obs-rectangle') as Konva.Rect;
	
		const width = x - startPoint.current.x;
		const height = y - startPoint.current.y;
	
		rect.width(width);
		rect.height(height);
		rect.x(startPoint.current.x);
		rect.y(startPoint.current.y);
		updateRectVertexHandles(userDrawnObs.current, rect, false, true);
	}, [isDrawing, stageRef, startPoint, updateRectVertexHandles, userDrawnObs]);

	const drawCirclePointerMove = useCallback(() => {
		if (!stageRef?.current || !isDrawing.current || !startPoint.current || !userDrawnObs?.current 
			|| !userDrawnObs?.current?.children?.length
		) return;
		const { x, y } = stageRef.current.getRelativePointerPosition() as Vector2d;
		const circle = userDrawnObs.current.findOne('.obs-circle') as Konva.Circle;
	
		const dx = x - startPoint.current.x;
		const dy = y - startPoint.current.y;
		const radius = Math.sqrt(dx * dx + dy * dy);
	
		circle.radius(radius);
		updateCircleVertexHandles(userDrawnObs.current, circle, false, true);
	}, [isDrawing, stageRef, startPoint, updateCircleVertexHandles, userDrawnObs]);

	const drawRectanglePointerUp = useCallback(() => {
		if (!isDrawing.current || !userDrawnObs?.current || !userDrawnObs?.current?.children?.length) return;
		
		const rect = userDrawnObs.current.findOne('.obs-rectangle') as Konva.Rect;
		const width = rect.width();
		const height = rect.height();
	
		// Prevent saving ~0-area completed rectangles
		if (Math.abs(width) == 0 || Math.abs(height) == 0) {
			if(!rect.getAttr('completed')){
				return;
			}
			userDrawnObs.current.destroy();
		} else if(!rect.getAttr('completed')) {
			const {coords, area} = getCoordinatesFromShapeWithArea(rect, scale.x, scale.y);
			dispatch(setManualObs({
				newlyAddedObs: {
					id: userDrawnObs.current.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0],
					coordinates: coords,
					userMapped: true,
					area,
					assessment: 'RAW',
					shapeType: 'RECT',
				}
			}));
			dispatch(pushUndoAction({
				type: 'ADD',
				obstructionId: userDrawnObs.current.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0],
			}));
			userDrawnObs.current.draggable(false);
			rect.setAttr('completed', true);
		}
		updateRectVertexHandles(userDrawnObs.current, rect, false, true);
		isDrawing.current = false;
		startPoint.current = null;
		userDrawnObs.current = null;
		markJustCompleted();
	}, [dispatch, isDrawing, scale.x, scale.y, startPoint, updateRectVertexHandles, userDrawnObs, markJustCompleted]);	

	const drawCirclePointerUp = useCallback(() => {
		if (!isDrawing.current || !userDrawnObs?.current || !userDrawnObs?.current?.children?.length) return;
		
		const circle = userDrawnObs.current.findOne('.obs-circle') as Konva.Circle;
		const radius = circle.radius();
	
		// Prevent saving 0-area completed circles
		if (radius <= 0) {
			if(!circle.getAttr('completed')){
				return;
			}
			userDrawnObs.current.destroy();
		} else {
			const { coords, area } = getCoordinatesFromShapeWithArea(circle, scale.x, scale.y);
			dispatch(setManualObs({
				newlyAddedObs: {
					id: userDrawnObs.current.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0],
					coordinates: coords,
					userMapped: true,
					area,
					assessment: 'CIRCLE',
					shapeType: 'CIRCLE',
				}
			}));
			dispatch(pushUndoAction({
				type: 'ADD',
				obstructionId: userDrawnObs.current.id().split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0],
			}));
			userDrawnObs.current.draggable(false);
			circle.setAttr('completed', true);
		}
		// normalize group before creating handles
		userDrawnObs.current.position({ x: 0, y: 0 });
		updateCircleVertexHandles(userDrawnObs.current, circle, false, true);
	
		isDrawing.current = false;
		startPoint.current = null;
		userDrawnObs.current = null;
		markJustCompleted();
	}, [dispatch, isDrawing, scale.x, scale.y, startPoint, updateCircleVertexHandles, userDrawnObs, markJustCompleted]);

	const deleteInProgressObstruction = useCallback(
		(group: Konva.Group) => {
			if(!deleteObstructionsMode)	return;
			const obsId = group.id().split(
				KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR
			)[0];

			const snapshot = (manualObsRef.current && manualObs[obsId]) 
      || null;

			if (!snapshot) return;

			dispatch(
				pushUndoAction({
					type: 'DELETE',
					obstructionId: obsId,
					snapshot,
					wasInProgress: true,
				})
			);
			dispatch(removeNewlyCreatedObs({ id: obsId }));

			group.destroy();

			if (userDrawnObs.current?.id() === group.id()) {
				userDrawnObs.current = null;
				isDrawing.current = false;
				startPoint.current = null;
			}

			stageRef?.current?.batchDraw();
		},
		[deleteObstructionsMode, manualObs, dispatch, userDrawnObs, stageRef, isDrawing, startPoint]
	);

	const handleDeleteObs = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
		if (!deleteObstructionsMode) return;

		const tappedObstructionGroup = findAncestorMatching(e.target ?? null, [
			KonvaGroupNames.inProgressUserDefinedObsGroup,
		]) as Konva.Group | null;

		if (tappedObstructionGroup) {
			deleteInProgressObstruction(tappedObstructionGroup);
		}
	}, [deleteObstructionsMode, deleteInProgressObstruction]);

	const handlePointerDown = useCallback(() => {
		if (isEditObsMode && !!selectedObstructionId) return;
		switch (obstructionShape) {
		case OBS_SHAPE.POLYGON:
			break;
		case OBS_SHAPE.RECT:
			drawRectanglePointerDown();
			break;
		case OBS_SHAPE.CIRCLE:
			drawCirclePointerDown();
			break;
		}
	}, [isEditObsMode, obstructionShape, drawRectanglePointerDown, drawCirclePointerDown, selectedObstructionId]);

	const handlePointerMove = useCallback(() => {
	// ignore any move while zooming
		if (stageRef?.current?.getAttr('isZooming')) {
			return;
		}
		switch (obstructionShape) {
		case OBS_SHAPE.POLYGON:
			break;
		case OBS_SHAPE.RECT:
			drawRectanglePointerMove();
			break;
		case OBS_SHAPE.CIRCLE:
			drawCirclePointerMove();
			break;
		}
	}, [obstructionShape, drawRectanglePointerMove, drawCirclePointerMove, stageRef]);

	const handlePointerUp = useCallback(() => {
		if (stageRef?.current?.getAttr('isZooming')) {
			return;
		}

		switch (obstructionShape) {
		case OBS_SHAPE.POLYGON:
			break; 
		case OBS_SHAPE.RECT:
			drawRectanglePointerUp();
			break;
		case OBS_SHAPE.CIRCLE:
			drawCirclePointerUp();
		}
	}, [obstructionShape, drawRectanglePointerUp, drawCirclePointerUp, stageRef]);

	const touchStart = useCallback((e: Konva.KonvaEventObject<TouchEvent>) => {
		if (!stageRef?.current) return;
		if (deleteObstructionsMode) {
			return;
		}

		// If second finger appears -> pinch/zoom
		if (e.evt && e.evt.touches && e.evt.touches.length > 1) {
			stageRef.current.setAttr('isZooming', true);
			pendingTouchRef.current = null;
			return;
		}

		// Single finger: record intent ONLY
		const pos = stageRef.current.getRelativePointerPosition();
		if (!pos) return;

		pendingTouchRef.current = {
			x: pos.x,
			y: pos.y,
		};

		// prevent browser gestures
		e.evt.preventDefault();
	}, [stageRef, deleteObstructionsMode]);

	const touchMove = useCallback((e: Konva.KonvaEventObject<TouchEvent>) => {
		if (!stageRef?.current) return;

		/* -----------------------------------------
	   1. PINCH DETECTION (HIGHEST PRIORITY)
	------------------------------------------ */
		if (e.evt && e.evt.touches && e.evt.touches.length > 1) {
			stageRef.current.setAttr('isZooming', true);
			pendingTouchRef.current = null;
			return;
		}

		/* -----------------------------------------
	   2. IGNORE WHILE ZOOMING
	------------------------------------------ */
		if (stageRef.current.getAttr('isZooming')) {
			return;
		}

		/* -----------------------------------------
	   3. CONFIRM SINGLE-TOUCH DRAW START
	   (THIS IS WHERE drawRectanglePointerDown
	    / drawCirclePointerDown IS CALLED)
	------------------------------------------ */
		if (pendingTouchRef.current && !isDrawing.current) {
			if (isEditObsMode && !!selectedObstructionId) {
				pendingTouchRef.current = null;
				return;
			}
			startPoint.current = pendingTouchRef.current;
			pendingTouchRef.current = null;

			switch (obstructionShape) {
			case OBS_SHAPE.RECT:
				drawRectanglePointerDown();
				break;
			case OBS_SHAPE.CIRCLE:
				drawCirclePointerDown();
				break;
			}

			// IMPORTANT: do NOT call handlePointerMove yet
			return;
		}

		/* -----------------------------------------
	   4. NORMAL DRAWING MOVE
	   (THIS IS THE ONLY PLACE
	    handlePointerMove IS ALLOWED)
	------------------------------------------ */
		if (isDrawing.current) {
			handlePointerMove();
		}
	}, [stageRef, isDrawing, startPoint, obstructionShape, drawRectanglePointerDown, drawCirclePointerDown, handlePointerMove, isEditObsMode, selectedObstructionId]);

	const touchEnd = useCallback((e: Konva.KonvaEventObject<TouchEvent>) => {
		const remaining = (e.evt && e.evt.touches) ? e.evt.touches.length : 0;

		// If zoom just ended, NEVER finalize drawing
		if (stageRef?.current?.getAttr('isZooming')) {
			if (remaining === 0) {
				stageRef.current.setAttr('isZooming', false);
				zoomJustEndedRef.current = true;
				requestAnimationFrame(() => {
					zoomJustEndedRef.current = false;
				});
			}
			pendingTouchRef.current = null;
			return;
		}

		if (remaining === 0) {
			pendingTouchRef.current = null;
			handlePointerUp();
		}
	}, [handlePointerUp, stageRef]);

	/* ---------------------------------------------------------------------- */
	/*                     Pointer / Touch Wiring                              */
	/* ---------------------------------------------------------------------- */

	useEffect(() => {
		if (!stageRef?.current || moveStage || rotateStage || !shouldListenClickEventsForDrawing) return;

		const stage = stageRef.current;

		stage.on('touchstart', touchStart);
		stage.on('touchmove', touchMove);
		stage.on('touchend', touchEnd);
		stage.on('touchcancel', touchEnd);
		stage.on('tap', drawPolygonPointerDown);

		stage.on('mousedown', handlePointerDown);
		stage.on('pointermove', handlePointerMove);
		stage.on('mousemove', drawPolygonPointerMove);
		stage.on('mouseup', handlePointerUp);
		stage.on('click', drawPolygonPointerDown);

		return () => {
			stage.off('touchstart', touchStart);
			stage.off('touchmove', touchMove);
			stage.off('touchend', touchEnd);
			stage.off('touchcancel', touchEnd);
			stage.off('tap', drawPolygonPointerDown);

			stage.off('mousedown', handlePointerDown);
			stage.off('pointermove', handlePointerMove);
			stage.off('mousemove', drawPolygonPointerMove);
			stage.off('mouseup', handlePointerUp);
			stage.off('click', drawPolygonPointerDown);
			stage?.setAttr('isZooming', false);
		};
	}, [handlePointerDown, handlePointerMove, handlePointerUp, stageRef, moveStage, rotateStage, isDrawing, shouldListenClickEventsForDrawing,
		startPoint, userDrawnObs, touchStart, touchMove, touchEnd, drawPolygonPointerDown, drawPolygonPointerMove]);

	useEffect(() => {
		if (!stageRef?.current) return;
		if (!deleteObstructionsMode) return;

		const stage = stageRef.current;
		stage.on('pointerdown', handleDeleteObs);

		return () => {
			stage.off('pointerdown', handleDeleteObs);
		};
	}, [stageRef, deleteObstructionsMode, handleDeleteObs]);

	/* ---------------------------------------------------------------------- */
	/*                     Removing unfinished shapes when switching shapes   */
	/* ---------------------------------------------------------------------- */

	useEffect(() => {
		return () => {
			if(userDrawnObs?.current && obstructionShape == OBS_SHAPE.POLYGON) {
				const lastPolygon = userDrawnObs?.current?.children?.[0] as Konva.Line;
				const previewLine = userDrawnObs?.current?.findOne('.obs-preview-line') as Konva.Line;
				const polygonVertices = userDrawnObs?.current?.find('.obs-polygon-vertex-handle') as Konva.Circle[];
				if(lastPolygon && lastPolygon.closed() === false) {
					lastPolygon.destroy();
					previewLine.destroy();
					polygonVertices.forEach((v) => v.destroy());
					userDrawnObs.current.destroy();
					return;
				}
				const { area } = getCoordinatesFromShapeWithArea(lastPolygon, scale.x, scale.y);

				if (area <= 0) {
					if (lastPolygon) lastPolygon.destroy();
					if (previewLine) previewLine.destroy();
					userDrawnObs?.current?.destroy();
				}

			}
			if(userDrawnObs?.current && (obstructionShape == OBS_SHAPE.RECT || obstructionShape == OBS_SHAPE.CIRCLE)) {
				const shape = obstructionShape == OBS_SHAPE.RECT ? 
					(userDrawnObs?.current?.findOne('.obs-rectangle') as Konva.Rect) :
					(userDrawnObs?.current?.findOne('.obs-circle') as Konva.Circle);
				if(!shape) return;
				if(!shape.getAttr('completed')) {
					if(obstructionShape == OBS_SHAPE.RECT) {
						const vertexCircles = userDrawnObs?.current?.find('.obs-rect-vertex-handle') as Konva.Circle[];
						vertexCircles.forEach((v) => v.destroy());
					}
					shape.destroy();
					userDrawnObs?.current?.destroy();
					return;
				}
				const { area } = getCoordinatesFromShapeWithArea(shape, scale.x, scale.y);

				if (area <= 0) {
					userDrawnObs?.current?.destroy();
				}
			}
		};
	}, [obstructionShape, scale.x, scale.y, userDrawnObs]);

	useEffect(() => {
	// Reset interaction flags (always safe)
		isDrawing.current = false;
		startPoint.current = null;
		pendingTouchRef.current = null;
		zoomJustEndedRef.current = false;

		// Destroy ONLY unfinished shapes
		if (userDrawnObs.current) {
			const group = userDrawnObs.current;

			const rect = group.findOne('.obs-rectangle') as Konva.Rect | null;
			const circle = group.findOne('.obs-circle') as Konva.Circle | null;
			const line = group.findOne('.obs-line') as Konva.Line | null;

			const isUnfinishedRect = rect && !rect.getAttr('completed');
			const isUnfinishedCircle = circle && !circle.getAttr('completed');
			const isUnfinishedPolygon = line && !group.getAttr('closed');

			if (isUnfinishedRect || isUnfinishedCircle || isUnfinishedPolygon) {
				group.destroy();
			}

			userDrawnObs.current = null;
		}

		// Clear transient zoom state
		stageRef?.current?.setAttr('isZooming', false);
	}, [isDrawing, obstructionShape, stageRef, startPoint, userDrawnObs]);

	// --------------------- Removes the in progress obs by undoing --------------------- */
	useEffect(() => {
		if (!stageRef?.current) return;

		const stage = stageRef.current;
		const obsDrawerGroup = stage.findOne(
			`.${KonvaGroupNames.obstructionDrawingGroup}`
		) as Konva.Group;

		if (!obsDrawerGroup) return;

		obsDrawerGroup.children.forEach((group) => {
			const obsId = group.id().split(
				KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR
			)[0];

			if (deletedManuallyAddedObstructionIDs.includes(obsId)) {
				group.destroy();
			}
		});

		stage.batchDraw();
	}, [deletedManuallyAddedObstructionIDs, stageRef]);

	useEffect(() => {
		if (!stageRef?.current || !manualObsRef.current) return;

		const obsDrawerGroup = manualObsRef.current;

		// Helper to configure the group
		const setupKonvaGroup = (obs: any, group: Konva.Group) => {
			// HARD RESET GROUP STATE
			group.stopDrag();
			group.position({ x: 0, y: 0 });
			group.offset({ x: 0, y: 0 });
			const isSelected = selectedObstructionId === obs.id;

			if (isSelected) {
				addDragHandlers(group);
			} else {
				group.draggable(false);
				group.off('dragstart dragend');
			}
			addSelectionHandlers(group, obs.id);

			const showHandles = !deleteObstructionsMode;
			const threshold = EDITING_ZONE_THRESHOLD / zoomLevelRef.current;

			const existingZone = group.findOne('.editing-zone');
			if (existingZone) existingZone.destroy();

			const addEditingZone = (zone: Konva.Shape) => {
				group.add(zone);
				zone.moveToBottom();
			};

			if (obs.shapeType === 'RECT') {
				const rect = group.findOne('.obs-rectangle') as Konva.Rect;
				if (rect) {
					rect.listening(true);
					updateRectVertexHandles(group, rect, isSelected, showHandles);
				}
			}

			if (obs.shapeType === 'POLYGON') {
				const line = group.findOne('.obs-line') as Konva.Line;
				if (line) {
					applyPolygonValidity(group, line, line.points());
					const isPointEditActive = isSelected && isPointEditMode;
					line.off('click.obsAddPoint tap.obsAddPoint click.obsDeletePoint tap.obsDeletePoint');
					if (isSelected) {
						line.on('click.obsAddPoint tap.obsAddPoint', (e) => {
							handleManualPolygonAddPoint(group, line, e);
						});
						line.on('click.obsDeletePoint tap.obsDeletePoint', (e) => {
							handleManualPolygonDeletePoint(group, line, e);
						});
					}
					if (isPointEditActive) {
						const editingZone = new Konva.Line({
							points: line.points(),
							closed: true,
							fill: 'transparent',
							stroke: 'transparent',
							listening: true,
							hitStrokeWidth: (OBSTRUCTION_ADD_POINT_HIT_STROKE + (threshold * 2)) / zoomLevelRef.current,
							name: 'editing-zone',
						});

						editingZone.on('click.obsAddPoint tap.obsAddPoint', (e) => {
							handleManualPolygonAddPoint(group, line, e);
						});
						editingZone.on('click.obsDeletePoint tap.obsDeletePoint', (e) => {
							handleManualPolygonDeletePoint(group, line, e);
						});

						addEditingZone(editingZone);
					}

					line.hitStrokeWidth(0);
					line.listening(true);
					updatePolygonVertexHandles(group, line.points(), isSelected, showHandles);
				}
			}

			if (obs.shapeType === 'CIRCLE') {
				const circle = group.findOne('.obs-circle') as Konva.Circle;
				if (circle) {
					circle.listening(true);
					updateCircleVertexHandles(group, circle, isSelected, showHandles);
				}
			}
		};
		
		Object.values(manualObs).forEach((obs) => {
			const existing = obsDrawerGroup.findOne(
				`#${obs.id}${KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR}${KONVA_OBS_GROUP.IN_PROGRESS_GROUP_NAME}`
			) as Konva.Group;

			if (!existing) {
				// 1. CREATION: Shape doesn't exist on canvas yet
				const group = recreateKonvaFromObstruction(obs, obsDrawerGroup, scale.x, scale.y);
				setupKonvaGroup(obs, group);
			} else {
				// 2. SYNC / UPDATE: Shape exists, let's make sure it matches Redux
				const shape = existing.findOne('.obs-rectangle') || existing.findOne('.obs-circle') || existing.findOne('.obs-line');
				if (shape) {
					const { coords } = getCoordinatesFromShapeWithArea(shape as any, scale.x, scale.y);
          
					// If the coordinates on the canvas don't match Redux (e.g., an Undo happened)
					// Destroy the out-of-sync shape and recreate it from the Redux state.
					if (JSON.stringify(coords) !== JSON.stringify(obs.coordinates)) {
						existing.destroy();
						const group = recreateKonvaFromObstruction(obs, obsDrawerGroup, scale.x, scale.y);
						setupKonvaGroup(obs, group);
					} else {
						setupKonvaGroup(obs, existing);
					}
				}
			}
		});

		stageRef.current.batchDraw();
	}, [addDragHandlers, addSelectionHandlers, applyPolygonValidity, deleteObstructionsMode, manualObs, scale.x, scale.y, stageRef, selectedObstructionId, isEditObsMode, dispatch,
		updateCircleVertexHandles, updatePolygonVertexHandles, updateRectVertexHandles, obstructionEditPointAction, handleManualPolygonAddPoint, handleManualPolygonDeletePoint]);

	useEffect(() => {
		if (!stageRef?.current) return;
		// update visibility of vertex circles based on deleteObstructionsMode

		if (deleteObstructionsMode) {
			Object.values(vertexRef.current).forEach((circles) => {
				circles.forEach((c) => c.visible(false));
			});
		}
		else if (!deleteObstructionsMode) {
			Object.values(vertexRef.current).forEach((circles) => {
				circles.forEach((c) => c.visible(true));
			});
		}
		stageRef.current.batchDraw();
	}, [deleteObstructionsMode, stageRef]);

	useEffect(() => {
		if (!stageRef?.current || !manualObsRef.current) return;

		const isDeletePointMode = isEditObsMode && obstructionEditPointAction === OBS_EDIT_POINT_ACTION.DELETE_POINT;
		const showPointerOnHover = deleteObstructionsMode;

		const handleEnter = () => {
			if (!showPointerOnHover) return;
			setCursor('pointer');
		};

		const handleLeave = () => {
			setFallbackCursor();
		};

		const groups = manualObsRef.current.find(
			`.${KonvaGroupNames.inProgressUserDefinedObsGroup}`
		) as Konva.Group[];

		// If delete-point mode, force pointer cursor globally
		if (isDeletePointMode) {
			groups.forEach((group) => {
				group.off('mouseenter', handleEnter);
				group.off('mouseleave', handleLeave);
			});
			setCursor('pointer');
		} else if (showPointerOnHover) {
			groups.forEach((group) => {
				group.on('mouseenter', handleEnter);
				group.on('mouseleave', handleLeave);
			});
		} else {
		// Remove listeners when exiting delete modes
			groups.forEach((group) => {
				group.off('mouseenter', handleEnter);
				group.off('mouseleave', handleLeave);
			});
			setFallbackCursor();
		}

		stageRef.current.batchDraw();

		return () => {
			groups.forEach((group) => {
				group.off('mouseenter', handleEnter);
				group.off('mouseleave', handleLeave);
			});
		};
	}, [deleteObstructionsMode, isEditObsMode, obstructionEditPointAction, setCursor, setFallbackCursor, stageRef]);

	useEffect(() => {
		if (!stageRef?.current || !enableEditObs) return;
		// Enforce strict cursor rules on state changes (selection/mode)
		setFallbackCursor();
	}, [enableEditObs, handleFallbackCursor, setCursor, stageRef]);

	const manualObsStateRef = useRef(manualObs);
	useEffect(() => {
		manualObsStateRef.current = manualObs;
	}, [manualObs]);

	useEffect(() => {
		if (!stageRef?.current) return;

		const newCircleRadius = getObstructionHandleSize(BASE_RADIUS, zoomLevel, handleZoomMode);
		const newSquareSize = getObstructionHandleSize(HANDLER_SIZE, zoomLevel, handleZoomMode);
		const newStrokeWidth = 1 / zoomLevel;
		const halfSquareSize = newSquareSize / 2;
		const squareHitStroke = OBSTRUCTION_HANDLE_HIT_STROKE / zoomLevel;
		const baseCircleHitStroke = OBSTRUCTION_HANDLE_HIT_STROKE / zoomLevel;
		const deleteCircleHitStroke = OBSTRUCTION_DELETE_POINT_HIT_STROKE / zoomLevel;
		const circleHitStroke = obstructionEditPointAction === OBS_EDIT_POINT_ACTION.DELETE_POINT
			? Math.max(deleteCircleHitStroke, baseCircleHitStroke)
			: baseCircleHitStroke;

		// Find all nodes matching the handle names
		const handles = stageRef.current.find(
			'.obs-polygon-vertex-handle, .obs-rect-vertex-handle, .obs-circle-vertex-handle'
		);

		handles.forEach((node) => {
			// Cast to Konva.Shape to access strokeWidth
			const shape = node as Konva.Shape;
			shape.strokeWidth(newStrokeWidth);

			if (shape instanceof Konva.Circle) {
				shape.radius(newCircleRadius);
				shape.hitStrokeWidth(circleHitStroke);
				shape.hitFunc(getHandleHitFunc('circle', newCircleRadius, circleHitStroke));
			} 
			else if (shape instanceof Konva.Rect) {
				// Get current center to keep the handle pinned to the vertex
				const currentCenterX = shape.x() + (shape.width() / 2);
				const currentCenterY = shape.y() + (shape.height() / 2);

				shape.width(newSquareSize);
				shape.height(newSquareSize);
      
				// Update position so the square remains centered on the vertex point
				shape.x(currentCenterX - halfSquareSize);
				shape.y(currentCenterY - halfSquareSize);
				shape.hitStrokeWidth(squareHitStroke);
				shape.hitFunc(getHandleHitFunc('square', newSquareSize, squareHitStroke));
			}
		});

		stageRef.current.batchDraw();
	}, [handleZoomMode, obstructionEditPointAction, stageRef, zoomLevel]);

	return (
		<Group
			name={KonvaGroupNames.obstructionDrawingGroup}
			listening={shouldListenClickEventsForDrawing || shouldListenClickEventsForDeleting || isEditObsMode}
			ref={manualObsRef}
		/>
	);
};

export default ObstructionDrawer;
